import { getTerminologyReferenceOccurrences } from './terminology-reference-editing';

export interface TerminologyWordingDiffSegment {
  text: string;
  changed: boolean;
}

export interface TerminologyWordingDiff {
  currentSegments: TerminologyWordingDiffSegment[];
  proposalSegments: TerminologyWordingDiffSegment[];
  hasChanges: boolean;
  /** The changed regions are conservative rather than a detailed word comparison. */
  isCoarse: boolean;
}

const MAX_TEXT_LENGTH = 20_000;
const MAX_LCS_CELLS = 100_000;
const graphemes = new Intl.Segmenter('en', { granularity: 'grapheme' });

function append(
  segments: TerminologyWordingDiffSegment[],
  text: string,
  changed: boolean,
): void {
  if (!text) return;
  const last = segments[segments.length - 1];
  if (last?.changed === changed) last.text += text;
  else segments.push({ text, changed });
}

/** Keep words, spaces, grapheme clusters and strict raw references indivisible. */
function units(text: string): string[] {
  const result: string[] = [];
  const ordinary = (value: string) => {
    let group = '',
      kind = '';
    for (const { segment } of graphemes.segment(value)) {
      const nextKind = /^[\p{L}\p{N}\p{M}_]+$/u.test(segment)
        ? 'word'
        : /^\s+$/u.test(segment)
          ? 'space'
          : '';
      if (group && (!nextKind || nextKind !== kind)) {
        result.push(group);
        group = '';
      }
      if (nextKind) {
        group += segment;
        kind = nextKind;
      } else {
        result.push(segment);
        kind = '';
      }
    }
    if (group) result.push(group);
  };
  let offset = 0;
  for (const reference of getTerminologyReferenceOccurrences(text)) {
    ordinary(text.slice(offset, reference.start));
    result.push(reference.rawText);
    offset = reference.end;
  }
  ordinary(text.slice(offset));
  return result;
}

/**
 * A presentation-only exact-text comparison. No HTML, grammar validation or merging is performed.
 * Detailed work is bounded; coarse output never labels unproven middle text as unchanged.
 */
export function compareTerminologyWording(
  current: string,
  proposal: string,
): TerminologyWordingDiff {
  const currentSegments: TerminologyWordingDiffSegment[] = [];
  const proposalSegments: TerminologyWordingDiffSegment[] = [];
  if (current === proposal) {
    append(currentSegments, current, false);
    append(proposalSegments, proposal, false);
    return {
      currentSegments,
      proposalSegments,
      hasChanges: false,
      isCoarse: false,
    };
  }
  if (current.length > MAX_TEXT_LENGTH || proposal.length > MAX_TEXT_LENGTH) {
    append(currentSegments, current, true);
    append(proposalSegments, proposal, true);
    return {
      currentSegments,
      proposalSegments,
      hasChanges: true,
      isCoarse: true,
    };
  }

  const before = units(current),
    after = units(proposal);
  let prefix = 0,
    suffix = 0;
  while (
    prefix < before.length &&
    prefix < after.length &&
    before[prefix] === after[prefix]
  )
    prefix += 1;
  while (
    suffix < before.length - prefix &&
    suffix < after.length - prefix &&
    before[before.length - suffix - 1] === after[after.length - suffix - 1]
  )
    suffix += 1;
  const beforeEnd = before.length - suffix,
    afterEnd = after.length - suffix;
  const left = before.slice(prefix, beforeEnd),
    right = after.slice(prefix, afterEnd);
  append(currentSegments, before.slice(0, prefix).join(''), false);
  append(proposalSegments, after.slice(0, prefix).join(''), false);

  const columns = right.length + 1;
  const isCoarse = (left.length + 1) * columns > MAX_LCS_CELLS;
  if (isCoarse) {
    append(currentSegments, left.join(''), true);
    append(proposalSegments, right.join(''), true);
  } else {
    const lengths = new Uint32Array((left.length + 1) * columns);
    for (let row = left.length - 1; row >= 0; row -= 1)
      for (let column = right.length - 1; column >= 0; column -= 1) {
        const index = row * columns + column;
        lengths[index] =
          left[row] === right[column]
            ? lengths[index + columns + 1] + 1
            : Math.max(lengths[index + columns], lengths[index + 1]);
      }
    let row = 0,
      column = 0;
    while (row < left.length || column < right.length) {
      if (
        row < left.length &&
        column < right.length &&
        left[row] === right[column]
      ) {
        append(currentSegments, left[row], false);
        append(proposalSegments, right[column], false);
        row += 1;
        column += 1;
      } else if (
        row < left.length &&
        (column === right.length ||
          lengths[(row + 1) * columns + column] >=
            lengths[row * columns + column + 1])
      ) {
        append(currentSegments, left[row], true);
        row += 1;
      } else {
        append(proposalSegments, right[column], true);
        column += 1;
      }
    }
  }
  append(currentSegments, before.slice(beforeEnd).join(''), false);
  append(proposalSegments, after.slice(afterEnd).join(''), false);
  return { currentSegments, proposalSegments, hasChanges: true, isCoarse };
}
